import { FC, useCallback, useEffect, useState } from 'react';
import { Button, Card, Grid, Group, Stack, Text } from '@mantine/core';
import SectionWrapper from '@components/SectionWrapper';
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  rectIntersection,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  AnalysisType,
  useAnalysisEditionStore,
} from '@stores/AnalysisEditionStore';
import { sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { snapCenterToCursor } from '@dnd-kit/modifiers';
import FieldItem from '@components/AnalysisEdition/FieldItem';
import FieldsColumn from '@components/AnalysisEdition/FieldsColumn';
import ExplorationColumn from '@components/AnalysisEdition/ExplorationColumn';
import PopulationsColumn from '@components/AnalysisEdition/PopulationsColumn';
import { useNavigate } from '@hooks/useNavigate';
import { DriversOutcomeModal } from '@components/AnalysisEdition/DriversOutcomeModal';
import { InlineEditableText } from '@components/InlineEditableText';
import { useCurrentProject } from '@hooks/useCurrentProject';
import { useUpdateProject } from '@apis/hooks/useUpdateProject';
import { useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { InternalBenchmarkAnalysisRequestModel } from '@models/ProjectAnalysisModel/InternalBenchmarkAnalysisModel';
import {
  DriversOfOutcomeAnalysisRequestModel,
  OutcomeConfiguration,
} from '@models/ProjectAnalysisModel/DriversOfOutcomeAnalysisModel';
import { useAppStore } from '@stores/AppStore';
import { notifications } from '@mantine/notifications';
import { RawVoiceAnalysisRequestModel } from '@models/ProjectAnalysisModel/RawVoiceAnalysisModel';
import { OvertimeComparisonAnalysisRequestModel } from '@models/ProjectAnalysisModel/OvertimeComparisonAnalysisModel';

interface CreateAnalysisPageProps {
  analysisType: AnalysisType;
}

const CreateAnalysisPage: FC<CreateAnalysisPageProps> = ({ analysisType }) => {
  const project = useCurrentProject();
  const setAnalysisType = useAnalysisEditionStore((s) => s.setAnalysisType);
  const populations = useAnalysisEditionStore((s) => s.populations);
  const setPopulations = useAnalysisEditionStore((s) => s.setPopulations);
  const onDragStart = useAnalysisEditionStore((s) => s.onDragStart);
  const onDragCancel = useAnalysisEditionStore((s) => s.onDragCancel);
  const onDragEnd = useAnalysisEditionStore((s) => s.onDragEnd);
  const dragging = useAnalysisEditionStore((s) => s.dragging);
  const dragField = useAnalysisEditionStore((s) => s.dragField);
  const selectedDistributionRows = useAnalysisEditionStore(
    (s) => s.selectedDistributionRows
  );
  const pointerSensor = useSensor(PointerSensor, {
    activationConstraint: { distance: 15 },
  });
  const keyboardSensor = useSensor(KeyboardSensor, {
    coordinateGetter: sortableKeyboardCoordinates,
  });
  const sensors = useSensors(pointerSensor, keyboardSensor);
  const navigate = useNavigate();
  const [showDriversOutcomeModal, setShowDriversOutcomeModal] = useState(false);
  const [analysisName, setAnalysisName] = useState('');
  const queryClient = useQueryClient();
  const [nameError, setNameError] = useState('');
  const [outcomeValues, setOutcomeValues] = useState<string[]>([]);
  const [featureValues, setFeatureValues] = useState<string[]>([]);
  const addAnalysisInProcess = useAppStore((s) => s.addAnalysisInProcess);

  const updateProject = useUpdateProject({
    onSuccess(data, variables) {
      queryClient.setQueryData(['project', data.projectId], data);
      console.log([...variables.newAnalyses, ...variables.updatedAnalyses][0].name)
      const newAnalysis = data.analyses.find(
        (a) =>
          a.name ===
          [...variables.newAnalyses, ...variables.updatedAnalyses][0].name
      );
      console.log(data)
      console.log(newAnalysis)
      navigate(`/projects/${data.projectId}/analyses/${newAnalysis.id}`);
      if (newAnalysis.type === AnalysisType.DRIVERS_OF_OUTCOME) {
        addAnalysisInProcess(data.projectId, newAnalysis.id);
        notifications.show({
          id: `analysis-created-${data.projectId}-${newAnalysis.id}`,
          color: 'blue',
          title: 'Analysis in process',
          loading: true,
          message: `The analysis ${newAnalysis.name} is processing. We will let you know when is ready.`,
          autoClose: false,
        });
      }
    },
  });

  useEffect(() => {
    setAnalysisType(analysisType);
    setPopulations([]);
  }, [analysisType]);

  useEffect(() => {
    const defaultName =
      analysisType === AnalysisType.INTERNAL_BENCHMARK
        ? 'Benchmark analysis'
        : analysisType === AnalysisType.RAW_VOICE ? 'Blank sheet analysis' : analysisType === AnalysisType.PROGRESS_OVER_TIME ? "Direct compare analysis" : 'Drivers of outcome analysis';
    const total = (project?.analyses ?? []).filter((a) =>
      a.name.includes(defaultName)
    ).length;
    setAnalysisName(defaultName + ` #${total + 1}`);
  }, [analysisType, project]);

  useEffect(() => {
    if (
      (project?.analyses ?? [])
        .map((a) => a.name.toLowerCase())
        .includes(analysisName.toLowerCase())
    ) {
      setNameError('This name is already used.');
    } else {
      setNameError('');
    }
  }, [project?.analyses, analysisName]);

  const createAnalysis = useCallback(() => {
    if (!project) {
      console.error('Project not loaded. Please refresh.');
      return;
    }

    let updatedProject;
    if (analysisType === AnalysisType.INTERNAL_BENCHMARK) {
      const benchmarkName = analysisName + ' - Benchmark';
      updatedProject = {
        ...project,
        newAnalyses: [
          new InternalBenchmarkAnalysisRequestModel(
            analysisName,
            '1.1.0',
            undefined,
            benchmarkName
          ),
        ],
        updatedAnalyses: [],
        deletedAnalyses: [],
      };
      const updatedPopulations = {};
      populations.forEach((population) => {
        updatedPopulations[population.title] = population.definition;
      });
      updatedProject.benchmarks[benchmarkName] = {
        Populations: updatedPopulations,
        GlobalFilter: {},
      };

    } else if (analysisType === AnalysisType.RAW_VOICE) {
      const benchmarkName = analysisName + ' - Raw Voice';
      updatedProject = {
        ...project,
        newAnalyses: [
          new RawVoiceAnalysisRequestModel(
            analysisName,
            '1.0.0',
            undefined,
            benchmarkName
          ),
        ],
        updatedAnalyses: [],
        deletedAnalyses: [],
      };

      const updatedPopulations = {};
      populations.forEach((population) => {
        updatedPopulations[population.title] = population.definition;
      });
      updatedProject.benchmarks[benchmarkName] = {
        Populations: updatedPopulations,
        GlobalFilter: {},
      };
    } else if (analysisType === AnalysisType.DRIVERS_OF_OUTCOME) {
      const formattedOutcomes = outcomeValues.map((outcome: string) => {
        const [dataset, column] = outcome.split('.');
        return {
          dataset,
          column,
        };
      });

      updatedProject = {
        ...project,
        newAnalyses: [
          new DriversOfOutcomeAnalysisRequestModel(
            analysisName,
            '1.1.0',
            populations[0],
            new OutcomeConfiguration(null, formattedOutcomes),
            featureValues,
            '',
            '',
            ''
          ),
        ],
        updatedAnalyses: [],
        deletedAnalyses: [],
      };
    } else if (analysisType === AnalysisType.PROGRESS_OVER_TIME) {
      const benchmarkName = analysisName + ' - Progress Over Time';
      updatedProject = {
        ...project,
        newAnalyses: [
          new OvertimeComparisonAnalysisRequestModel(
            analysisName,
            '1.0.0',
            populations.map((p) => p.title),
            benchmarkName
          ),
        ],
        updatedAnalyses: [],
        deletedAnalyses: [],
      };

      const updatedPopulations = {};
      populations.forEach((population) => {
        updatedPopulations[population.title] = population.definition;
      });
      updatedProject.benchmarks[benchmarkName] = {
        Populations: updatedPopulations,
        GlobalFilter: {},
      };
    }
    updateProject.mutate(updatedProject);
  }, [project, analysisName, populations, outcomeValues, featureValues]);

  return (
    <SectionWrapper isFullHeight showBreadcrumbs>
      <DriversOutcomeModal
        opened={showDriversOutcomeModal}
        onConfirm={() => {
          setShowDriversOutcomeModal(false);
          createAnalysis();
        }}
        onClose={() => setShowDriversOutcomeModal(false)}
        outcomeValues={outcomeValues}
        setOutcomeValues={setOutcomeValues}
        featureValues={featureValues}
        setFeatureValues={setFeatureValues}
      />
      <DndContext
        autoScroll={false}
        onDragStart={(e) => onDragStart(e.active)}
        onDragCancel={onDragCancel}
        onDragEnd={(e) => onDragEnd(e.over)}
        sensors={sensors}
        collisionDetection={rectIntersection}
        modifiers={[snapCenterToCursor]}
      >
        <Stack style={{ flexGrow: 1 }}>
          <Stack spacing={0}>
            <InlineEditableText
              textStyle={{
                fontWeight: 700,
                fontSize: '1.375rem',
                lineHeight: 1.4,
              }}
              value={analysisName}
              onChange={setAnalysisName}
            />
            {/*<Text color="dimmed" size="sm">{`Define population${ analysisType === AnalysisType.INTERNAL_BENCHMARK ? "s" : ""}`}</Text> */}
            {nameError && (
              <Text color="red" size="sm">
                {nameError}
              </Text>
            )}
          </Stack>
          <Stack mt="md" style={{ flexGrow: 1 }}>
            <DragOverlay>
              {dragging === 'column' && dragField && (
                <FieldItem value={dragField} isDraggedItem />
              )}
              {dragging === 'row' && (
                <Stack spacing={2} style={{ width: 200 }}>
                  {selectedDistributionRows.length === 1 ? (
                    <RowOverlay text="Item 1" />
                  ) : selectedDistributionRows.length === 2 ? (
                    <>
                      <RowOverlay text="Item 1" />
                      <RowOverlay text="Item 2" />
                    </>
                  ) : (
                    <>
                      <RowOverlay text="Item 1" />
                      <RowOverlay text="Item 2" />
                      <RowOverlay text="Item ..." />
                    </>
                  )}
                </Stack>
              )}
            </DragOverlay>

            <Grid style={{ flexGrow: 1 }} gutter="xl">
              <Grid.Col
                span={2}
                style={{ display: 'flex', flexDirection: 'column' }}
              >
                <FieldsColumn />
              </Grid.Col>

              <Grid.Col
                span={8}
                style={{ display: 'flex', flexDirection: 'column' }}
              >
                <ExplorationColumn />
              </Grid.Col>

              <Grid.Col
                span={2}
                style={{ display: 'flex', flexDirection: 'column' }}
              >
                <PopulationsColumn />
              </Grid.Col>
            </Grid>
          </Stack>

          <Group mt="lg" position="apart">
            <Text c="red" size="xs">
              {
                ((updateProject.error as AxiosError)?.response?.data as any)
                  ?.Message
              }
            </Text>
            <Group>
              <Button
                onClick={() => navigate(`/projects/${project.projectId}`)}
                variant="subtle"
                color="dark"
              >
                Cancel
              </Button>
              <Button
                onClick={() =>
                  analysisType !== AnalysisType.DRIVERS_OF_OUTCOME
                    ? createAnalysis()
                    : setShowDriversOutcomeModal(true)
                }
                loading={updateProject.isPending}
                disabled={
                  populations.length === 0 ||
                  analysisName.length <= 3 ||
                  !!nameError
                }
              >
                {analysisType !== AnalysisType.DRIVERS_OF_OUTCOME
                  ? 'Create analysis'
                  : 'Next'}
              </Button>
            </Group>
          </Group>
        </Stack>
      </DndContext>
    </SectionWrapper>
  );
};

const RowOverlay: FC<{ text: string }> = ({ text }) => (
  <Card
    bg="#e7f5ff"
    p="xs"
    py={2}
    radius="sm"
    style={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}
  >
    <Text color="#228be6" size="sm" weight={700}>
      {text}
    </Text>
  </Card>
);

export default CreateAnalysisPage;
